/**
 * drawingprimitive.ts — ONE series primitive that renders all four hand-drawn tools.
 *
 * ONE PRIMITIVE, NOT FOUR, and that is the load-bearing decision. `chartapp/README.md`
 * records that "a third primitive with its own coordinate math is how the break-label
 * pass shipped broken three times", and this feature adds four shapes at once. They
 * share one `_at()` projection, one hit-test walk and one label pass, so a trend line and
 * a fib cannot disagree about where price 100 is.
 *
 * WHAT IT DOES NOT DO
 * -------------------
 * It computes no fib. A `fib` drawing arrives with `levels: [{ratio, price}]` already
 * computed by `ict.ranges.fib_levels` server-side — see `drawings.ts`'s header for why
 * (the OTE band's unconditional `ote_zone(hi, lo)` was on the wrong side of EQ on 69.6%
 * of degree-1 series, and a fib in JS would be the same bug in a place the user draws by
 * hand). The DECISIONS about snapping and hit geometry live in `drawings.ts` where vitest
 * can reach them; this file converts prices and times to pixels.
 *
 * THE DRAG DISCIPLINE IS COPIED FROM `ticketprimitive.ts`, EXACTLY
 * ---------------------------------------------------------------
 * `ISeriesPrimitiveBase.hitTest(x, y)` is the supported hook (present in
 * lightweight-charts 4.2.3, the pinned version) and it lets the LIBRARY decide what the
 * cursor is over in the same coordinate system it drew with — a div overlay would
 * re-derive the price scale and disagree after every zoom.
 *
 * `handleScroll`/`handleScale` are toggled off on grab and restored on pointerup AND
 * pointercancel. That second one is not defensive padding: a touch interrupted by the OS
 * never sends pointerup, and a lost restore leaves the chart permanently unpannable with
 * no visible cause. Restoring happens BEFORE the drop callback runs, so an exception in
 * the caller cannot freeze the chart either.
 */
import type {
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  ISeriesPrimitivePaneRenderer,
  ISeriesPrimitivePaneView,
  PrimitiveHoveredItem,
  SeriesAttachedParameter,
  SeriesType,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';

import {
  HIT_TOLERANCE_PX,
  hitTestDrawing,
  type Drawing,
  type ProjectedDrawing,
} from './drawings';
// The SHARED plate renderer: collision-tested and watermark-aware. Writing a private
// label pass here is how this feature first shipped 14 overlapping pairs among 8 labels.
import { drawPlate, type PlateBox } from './levelplate';

/** fancy-canvas' rendering scope, structurally re-declared — see `rangeprimitive.ts`. */
type Scope = {
  context: CanvasRenderingContext2D;
  horizontalPixelRatio: number;
  verticalPixelRatio: number;
  bitmapSize: { width: number; height: number };
};
type Target = { useBitmapCoordinateSpace: (f: (scope: Scope) => void) => void };

/** The drawing palette. One hue for all four kinds: these are the USER's marks, and
 *  giving each tool its own colour would make them compete with the computed overlays
 *  (teal/brick BOS-CHoCH, amber/violet liquidity, green/red FVG) whose colours MEAN
 *  something. Selection is shown by weight, not hue. */
const INK = 'rgba(232, 232, 240, 0.95)';
const INK_DIM = 'rgba(232, 232, 240, 0.55)';
const RECT_FILL = 'rgba(232, 232, 240, 0.08)';
/** Fib levels are dimmer than the leg that defines them, so the box reads first. */
const FIB_INK = 'rgba(224, 192, 96, 0.85)';
const FIB_DIM = 'rgba(224, 192, 96, 0.45)';

export type DrawingCallbacks = {
  /** A drawing was clicked (or the empty pane was — `null`). Selection is the caller's. */
  onSelect: (id: string | null) => void;
  /** An anchor was dropped. `index` is which anchor of that drawing moved. */
  onAnchorDrop: (id: string, index: number, time: number, price: number) => void;
};

export class DrawingPrimitive implements ISeriesPrimitive<Time> {
  private _chart: IChartApi | null = null;
  private _series: ISeriesApi<SeriesType, Time> | null = null;
  private _requestUpdate: (() => void) | null = null;
  private _data: Drawing[] = [];
  private _selected: string | null = null;
  private _labelFor: (d: Drawing) => string[] = () => [];
  private _cb: DrawingCallbacks | null = null;
  private _detach: (() => void) | null = null;
  /** `id:index` of the anchor being dragged. */
  private _dragging: { id: string; index: number } | null = null;
  private _savedScroll: unknown = null;
  private readonly _views: ISeriesPrimitivePaneView[];

  constructor() {
    this._views = [
      {
        // Rect fills UNDER the candles, so a box never hides the price action it frames
        // — the same layering `rangeprimitive.ts` chose for OTE/FVG zones.
        zOrder: () => 'bottom',
        renderer: (): ISeriesPrimitivePaneRenderer => ({
          draw: () => {},
          drawBackground: (t) => this._drawFills(t as unknown as Target),
        }),
      },
      {
        zOrder: () => 'top',
        renderer: (): ISeriesPrimitivePaneRenderer => ({
          draw: (t) => this._drawLines(t as unknown as Target),
        }),
      },
    ];
  }

  attached(param: SeriesAttachedParameter<Time, SeriesType>): void {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
    this._wirePointer();
  }

  detached(): void {
    this._detach?.();
    this._detach = null;
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews(): readonly ISeriesPrimitivePaneView[] {
    return this._views;
  }

  setCallbacks(cb: DrawingCallbacks): void {
    this._cb = cb;
  }

  /** How each drawing labels itself. Injected so the price FORMATTING rule stays in
   *  `overlays.priceText` — one formatter for the whole app. */
  setLabeller(fn: (d: Drawing) => string[]): void {
    this._labelFor = fn;
  }

  /** Replace the set. `[]` clears the layer — which is what an asset switch must do. */
  setData(d: Drawing[]): void {
    this._data = d;
    this._requestUpdate?.();
  }

  setSelected(id: string | null): void {
    if (id === this._selected) return;
    this._selected = id;
    this._requestUpdate?.();
  }

  get selected(): string | null {
    return this._selected;
  }

  /** What this layer is actually DRAWING. Read-only, and distinct from the caller's copy of the
   *  server payload on purpose: with one layer per pane, "pane-htf holds this drawing" can only be
   *  answered by the object that paints pane-htf. A test that asserted on the payload instead would
   *  pass while nothing reached the canvas -- which is exactly the shipped bug. */
  get data(): readonly Drawing[] {
    return this._data;
  }

  /** Drawings, projected to CSS pixels. The one place time/price becomes x/y. */
  private _projected(): ProjectedDrawing[] {
    const ts = this._chart?.timeScale();
    const series = this._series;
    if (!ts || !series) return [];
    return this._data.map((d) => ({
      id: d.id,
      kind: d.kind,
      points: (d.kind === 'fib' && d.levels?.length
        // A fib is hit on its LEVEL lines, so it is projected as those: one point per
        // ratio, at the LEG'S LEFT EDGE in x.
        //
        // The left edge is `min(anchor x)`, not `anchors[0].x`, and that was a real bug
        // caught by a test: projecting every level at the first anchor's time collapsed
        // the hit region to a 6px column while the levels are DRAWN from the leg's left
        // edge to the pane's right edge (`_drawLines`). The shape you can click has to be
        // the shape you can see, so both anchors' x are considered here and
        // `hitTestDrawing` extends the region rightwards to the pane edge.
        ? (() => {
          const xs = d.anchors
            .map((a) => ts.timeToCoordinate(a.time as UTCTimestamp))
            .filter((x) => x != null)
            .map(Number);
          const left = xs.length ? Math.min(...xs) : null;
          return d.levels.map((l) => {
            const y = series.priceToCoordinate(l.price);
            return left == null || y == null ? null : { x: left, y: Number(y) };
          });
        })()
        : d.anchors.map((a) => {
          const x = ts.timeToCoordinate(a.time as UTCTimestamp);
          const y = series.priceToCoordinate(a.price);
          return x == null || y == null ? null : { x, y };
        })),
    }));
  }

  /**
   * Hit test, in CSS coordinates, delegating the GEOMETRY to `drawings.hitTestDrawing`.
   *
   * The decision is in the pure module so a test can drive every kind's near-miss cases;
   * this only supplies the projection and the cursor style. Without a `cursorStyle`
   * nothing on screen suggests the mark can be selected at all.
   */
  hitTest(x: number, y: number): PrimitiveHoveredItem | null {
    const w = this._chart?.chartElement().clientWidth ?? 0;
    const id = hitTestDrawing(this._projected(), x, y, w);
    if (!id) return null;
    return { cursorStyle: 'pointer', externalId: id, zOrder: 'top' };
  }

  /** The ANCHOR handle under a CSS coordinate — a corner grab, not the whole shape. */
  private _anchorAt(x: number, y: number): { id: string; index: number } | null {
    const ts = this._chart?.timeScale();
    const series = this._series;
    if (!ts || !series) return null;
    for (const d of this._data) {
      // Only the SELECTED drawing exposes handles. Otherwise every stray click near any
      // mark would start a drag, and a chart full of levels becomes impossible to pan.
      if (d.id !== this._selected) continue;
      for (let i = 0; i < d.anchors.length; i++) {
        const cx = ts.timeToCoordinate(d.anchors[i].time as UTCTimestamp);
        const cy = series.priceToCoordinate(d.anchors[i].price);
        if (cx == null || cy == null) continue;
        if (Math.hypot(x - cx, y - cy) <= HIT_TOLERANCE_PX * 1.5) return { id: d.id, index: i };
      }
    }
    return null;
  }

  private _wirePointer(): void {
    const chart = this._chart;
    const series = this._series;
    if (!chart || !series) return;
    const el = chart.chartElement();

    const at = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const priceAt = (y: number): number | null => {
      const p = series.coordinateToPrice(y);
      return p == null ? null : Number(p);
    };
    const timeAt = (x: number): number | null => {
      const t = chart.timeScale().coordinateToTime(x);
      return t == null ? null : Number(t);
    };

    const onDown = (e: PointerEvent) => {
      const { x, y } = at(e);
      const handle = this._anchorAt(x, y);
      if (handle) {
        this._dragging = handle;
        // Suppress the chart's own pan for the duration, or dragging an anchor scrolls
        // the time scale and the anchor looks stuck while the candles slide.
        this._savedScroll = chart.options().handleScroll;
        chart.applyOptions({ handleScroll: false, handleScale: false });
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Not a handle: a plain click SELECTS (or clears the selection). Deliberately does
      // NOT preventDefault, so a click on empty pane still behaves like a chart click.
      const hitId = hitTestDrawing(this._projected(), x, y,
                                   el.clientWidth ?? 0);
      this._cb?.onSelect(hitId);
    };

    const onMove = (e: PointerEvent) => {
      if (!this._dragging) return;
      const { x, y } = at(e);
      const price = priceAt(y);
      const time = timeAt(x);
      if (price === null || time === null || !Number.isFinite(price) || price <= 0) return;
      // Live preview: mutate the local copy so the shape follows the cursor, and let the
      // caller persist only on drop. A POST per pointermove would be ~60 writes a second.
      const d = this._data.find((q) => q.id === this._dragging!.id);
      if (d) {
        d.anchors[this._dragging.index] = { time, price };
        this._requestUpdate?.();
      }
      e.preventDefault();
    };

    const release = (e: PointerEvent) => {
      if (!this._dragging) return;
      const { id, index } = this._dragging;
      this._dragging = null;
      // Restore FIRST — an exception in the drop handler must not leave the chart
      // permanently unpannable (the failure is silent, which is what makes it bad).
      chart.applyOptions({
        handleScroll: (this._savedScroll ?? true) as never,
        handleScale: true,
      });
      const { x, y } = at(e);
      const price = priceAt(y);
      const time = timeAt(x);
      if (price !== null && time !== null && Number.isFinite(price) && price > 0) {
        this._cb?.onAnchorDrop(id, index, time, price);
      }
    };

    el.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', release, true);
    window.addEventListener('pointercancel', release, true);
    this._detach = () => {
      el.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
    };
  }

  /** `(time, price)` -> bitmap, or null when either is off-scale. */
  private _at(time: number, price: number, hr: number, vr: number) {
    const x = this._chart?.timeScale().timeToCoordinate(time as UTCTimestamp);
    const y = this._series?.priceToCoordinate(price);
    return x == null || y == null ? null : { x: x * hr, y: y * vr };
  }

  private _drawFills(target: Target): void {
    if (!this._series) return;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr,
                                      verticalPixelRatio: vr }) => {
      for (const d of this._data) {
        if (d.kind !== 'rect') continue;
        const a = this._at(d.anchors[0].time, d.anchors[0].price, hr, vr);
        const b = this._at(d.anchors[1].time, d.anchors[1].price, hr, vr);
        if (!a || !b) continue;
        ctx.fillStyle = RECT_FILL;
        ctx.fillRect(Math.min(a.x, b.x), Math.min(a.y, b.y),
                     Math.abs(b.x - a.x), Math.abs(b.y - a.y));
      }
    });
  }

  private _drawLines(target: Target): void {
    if (!this._series) return;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr,
                                      verticalPixelRatio: vr, bitmapSize }) => {
      for (const d of this._data) {
        const on = d.id === this._selected;
        const ink = d.kind === 'fib' ? (on ? FIB_INK : FIB_DIM) : (on ? INK : INK_DIM);
        ctx.save();
        ctx.strokeStyle = ink;
        ctx.lineWidth = Math.max(1, Math.round((on ? 2 : 1) * hr));

        if (d.kind === 'trendline') {
          const a = this._at(d.anchors[0].time, d.anchors[0].price, hr, vr);
          const b = this._at(d.anchors[1].time, d.anchors[1].price, hr, vr);
          if (a && b) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        } else if (d.kind === 'hline') {
          const a = this._at(d.anchors[0].time, d.anchors[0].price, hr, vr);
          if (a) {
            // A RAY from the anchor bar rightwards, never the full pane: the level did
            // not exist before the bar the user marked, and drawing it leftwards claims
            // it did. Same rule `rangeprimitive.ts` records for computed levels.
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(bitmapSize.width, a.y);
            ctx.stroke();
          }
        } else if (d.kind === 'rect') {
          const a = this._at(d.anchors[0].time, d.anchors[0].price, hr, vr);
          const b = this._at(d.anchors[1].time, d.anchors[1].price, hr, vr);
          if (a && b) {
            ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y),
                           Math.abs(b.x - a.x), Math.abs(b.y - a.y));
          }
        } else if (d.kind === 'fib' && d.levels?.length) {
          const x0 = this._at(d.anchors[0].time, d.anchors[0].price, hr, vr);
          const x1 = this._at(d.anchors[1].time, d.anchors[1].price, hr, vr);
          if (x0 && x1) {
            const left = Math.min(x0.x, x1.x);
            for (const lv of d.levels) {
              const y = this._series?.priceToCoordinate(lv.price);
              if (y == null) continue;
              const by = y * vr;
              // Levels run from the leg's left edge to the pane's right edge: a fib is a
              // forward-looking tool, and bounding it at the leg would hide every level
              // exactly where price is about to test it.
              ctx.beginPath();
              // 0 and 1 are the leg's own ends — solid; the rest dashed, so the box the
              // user drew stays distinguishable from the levels derived from it.
              if (lv.ratio !== 0 && lv.ratio !== 1) ctx.setLineDash([4 * hr, 4 * hr]);
              else ctx.setLineDash([]);
              ctx.moveTo(left, by);
              ctx.lineTo(bitmapSize.width, by);
              ctx.stroke();
            }
            ctx.setLineDash([]);
          }
        }

        // Anchor handles, on the selected drawing only — the affordance that says the
        // shape can be reshaped. Without them a selected drawing looks merely highlighted.
        if (on) {
          const pts = d.anchors
            .map((a) => this._at(a.time, a.price, hr, vr))
            .filter((p): p is { x: number; y: number } => p !== null);
          ctx.fillStyle = ink;
          for (const p of pts) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, 4 * hr, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.restore();
      }

      // Labels last, so no line is drawn over its own text.
      this._drawLabels(ctx, hr, vr, bitmapSize);
    });
  }

  private _drawLabels(
    ctx: CanvasRenderingContext2D, hr: number, vr: number,
    bitmapSize: { width: number; height: number },
  ): void {
    // COLLISION-TESTED, via the shared plate renderer. My first version wrote its own
    // `fillRect` + `fillText` at a fixed right-edge x with no collision test at all —
    // the known-bad pattern this repo shipped three times (192/208 labels overlapping,
    // then BOS 0 / CHoCH 21). Measured on the live chart with two fibs 0.1% apart plus
    // an hline in the same band: **14 overlapping pairs among 8 labels**, including
    // `0 (63225)` and `hline 63256.6125` at dy=0 — drawn exactly on top of each other.
    //
    // `drawPlate` steps each plate RIGHT past anything already `taken` on its row, and
    // dodges the TradingView watermark. It requires `taken` in y ORDER for its
    // single-pass step to clear every overlap, so rows are sorted before drawing.
    const rows: { y: number; text: string; color: string; emphasis: boolean }[] = [];
    for (const d of this._data) {
      const texts = this._labelFor(d);
      if (!texts.length) continue;
      const on = d.id === this._selected;
      // A fib carries one label PER LEVEL (ratio + price, per Andrei's reference); the
      // other kinds carry one. An UNSELECTED fib stays quiet: 7 labels x N fibs is the
      // confetti above, and the box itself still carries the geometry.
      if (d.kind === 'fib' && d.levels?.length) {
        if (!on) continue;
        d.levels.forEach((lv, i) => {
          if (texts[i]) rows.push({ y: lv.price, text: texts[i], color: FIB_INK, emphasis: on });
        });
      } else if (texts[0]) {
        rows.push({
          y: d.anchors[0].price, text: texts[0],
          color: on ? INK : INK_DIM, emphasis: on,
        });
      }
    }
    const taken: PlateBox[] = [];
    for (const r of rows
      .map((r) => ({ ...r, py: this._series?.priceToCoordinate(r.y) }))
      // Off-pane text is DROPPED, not clamped: `priceToCoordinate` extrapolates
      // (measured y up to 537 on a 298px pane) and a clamped label would pile onto the
      // edge advertising a level nobody can see.
      .filter((r) => r.py != null && r.py >= 0 && r.py <= bitmapSize.height / vr)
      .sort((a, b) => Number(a.py) - Number(b.py))) {
      taken.push(drawPlate(ctx, r.text, r.color, Number(r.py) * vr, hr, vr, taken, {
        emphasis: r.emphasis, paneHeight: bitmapSize.height / vr,
      }));
    }
  }
}
